import React, { useCallback, useState, useEffect, memo } from "react";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import { Space, Col } from "antd";
import {
  LikeOutlined,
  DislikeTwoTone,
  DislikeOutlined,
  LikeTwoTone,
  CopyOutlined,
  CopyTwoTone
} from "@ant-design/icons";
import { postFeedbackToMessage as postFeedbackToMessageAPI } from "../../api/api";
import { useFetch } from "../../hooks/fetch";
import ErrorMessage from "../customComponent/customMessages/ErrorMessage";
import { useTranslation } from "react-i18next";
import { MessageActionButton } from "./MessageActionButton";
import configureStore from "../../store/store";
import { useDashboardBucketsTrackingEvent } from "../../utils/pendo/hooks";
import { MESSAGE_ACTIONS, PENDO_EVENTS } from "./Constants";
import FeedbackForm from "./FeedbackForm";
import { FeedbackFormWrapperStyle } from "./Styles";

const MessageActions = memo(({ conversationId, messageId, messageContent, messageFeedback }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const { trackEvent } = useDashboardBucketsTrackingEvent();
  const [localFeedback, setLocalFeedback] = useState(messageFeedback || null);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [postFeedbackToMessage, postFeedbackToMessageResult, postFeedbackToIsLoading, postFeedbackToMessageError] =
    useFetch(postFeedbackToMessageAPI);

  useEffect(() => {
    if (postFeedbackToMessageError) {
      ErrorMessage(t("ai_chat_bot.errors.failed_message_feedback_submission"));
      setLocalFeedback(null);
    }
  }, [postFeedbackToMessageError]);

  useEffect(() => {
    if (postFeedbackToMessageResult?.data) {
      setLocalFeedback(postFeedbackToMessageResult.data);
    }
  }, [postFeedbackToMessageResult]);

  const handleLike = useCallback(() => {
    trackEvent(PENDO_EVENTS.ANSWER_RATED, { conversationId, messageId, type: MESSAGE_ACTIONS.LIKE });
    const newFeedback = localFeedback === MESSAGE_ACTIONS.LIKE ? null : MESSAGE_ACTIONS.LIKE;
    setLocalFeedback(newFeedback);
    postFeedbackToMessage(conversationId, messageId, newFeedback);
    // Hide feedback form if it was showing
    setShowFeedbackForm(false);
  }, [conversationId, messageId, localFeedback]);

  const handleDislike = useCallback(() => {
    trackEvent(PENDO_EVENTS.ANSWER_RATED, { conversationId, messageId, type: MESSAGE_ACTIONS.DISLIKE });
    const newFeedback = localFeedback === MESSAGE_ACTIONS.DISLIKE ? null : MESSAGE_ACTIONS.DISLIKE;
    setLocalFeedback(newFeedback);
    postFeedbackToMessage(conversationId, messageId, newFeedback);

    setShowFeedbackForm(true);

    if (window.scrollChatToBottom) {
      setTimeout(() => {
        window.scrollChatToBottom();
      }, 100);
    }
  }, [conversationId, messageId, localFeedback]);

  const handleCopy = useCallback(async () => {
    try {
      trackEvent(PENDO_EVENTS.COPIED_ANSWER, { conversationId, messageId });
      const decodedContent = messageContent ? decodeURIComponent(messageContent) : "";
      await navigator.clipboard.writeText(decodedContent);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 1000);
    } catch (err) {
      ErrorMessage(t("ai_chat_bot.errors.failed_copying_message"));
    }
  }, [messageContent, conversationId, messageId, trackEvent, t]);

  const handleCloseFeedbackForm = useCallback(() => {
    setShowFeedbackForm(false);
  }, []);

  const isLikeActive = localFeedback === MESSAGE_ACTIONS.LIKE;
  const isDislikeActive = localFeedback === MESSAGE_ACTIONS.DISLIKE;

  return (
    <>
      <Space
        direction="horizontal"
        size={0}
        style={{
          display: "flex",
          justifyContent: "start",
          width: "62px",
          padding: "1px 2px",
          height: "16px",
          backgroundColor: "transparent"
        }}
      >
        <MessageActionButton
          style={{ width: "12px", height: "14px" }}
          icon={copied ? <CopyTwoTone twoToneColor="#5416BF" /> : <CopyOutlined />}
          onClick={handleCopy}
          tooltip={t("ai_chat_bot.message_actions.copy_message")}
        />
        <MessageActionButton
          style={{ width: "14px", height: "12px" }}
          icon={isLikeActive ? <LikeTwoTone twoToneColor="#5416BF" /> : <LikeOutlined />}
          onClick={handleLike}
          disabled={postFeedbackToIsLoading}
          tooltip={t("ai_chat_bot.message_actions.like_message")}
        />
        <MessageActionButton
          style={{ width: "14px", height: "12px" }}
          icon={isDislikeActive ? <DislikeTwoTone twoToneColor="#5416BF" /> : <DislikeOutlined />}
          onClick={handleDislike}
          disabled={postFeedbackToIsLoading}
          tooltip={t("ai_chat_bot.message_actions.dislike_message")}
        />
      </Space>
      {showFeedbackForm && (
        <Col style={FeedbackFormWrapperStyle}>
          <FeedbackForm conversationId={conversationId} messageId={messageId} onClose={handleCloseFeedbackForm} />
        </Col>
      )}
    </>
  );
});

const MessageActionsElement = memo(({ conversationId, messageId, messageContent, messageFeedback }) => {
  return (
    <MessageActions
      conversationId={conversationId}
      messageId={messageId}
      messageContent={messageContent}
      messageFeedback={messageFeedback}
    />
  );
});

// Register web component
const registerMessageActionsWebComponent = () => {
  class MessageActionsWebComponent extends HTMLElement {
    constructor() {
      super();
      this.root = document.createElement("div");
      this.appendChild(this.root);
    }

    connectedCallback() {
      const conversationId = this.getAttribute("conversation-id");
      const messageId = this.getAttribute("message-id");
      const messageContent = this.getAttribute("message-content");
      const messageFeedback = this.getAttribute("message-feedback");

      ReactDOM.render(
        <Provider store={configureStore(window.REDUX_INITIAL_DATA)}>
          <MessageActionsElement
            conversationId={conversationId}
            messageId={messageId}
            messageContent={messageContent}
            messageFeedback={messageFeedback}
          />
        </Provider>,
        this.root
      );
    }

    disconnectedCallback() {
      ReactDOM.unmountComponentAtNode(this.root);
    }
  }

  if (!customElements.get("message-actions")) {
    customElements.define("message-actions", MessageActionsWebComponent);
  }
};

export { registerMessageActionsWebComponent };
